"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import CompanyService from "@/api/companies.service";
import { Input } from "@/components/ui/input"
import { SendIcon, Loader2, ArrowLeft, Building2 } from "lucide-react"
import { useToast } from "@/hooks/use-toast";
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import countries from "@/components/Countries"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  emailAddress: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
  phoneNumber: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  website: z.string().optional()
})

export type Company = {
  id: number
  publicId: string
  name: string
  emailAddress: string
  phoneNumber: string
  address: string
  city: string
  state: string
  country: string
  postalCode?: string
  website?: string
  timezone: string
  dateUpdated: string
  dateCreated: string
}

export default function CreateCompanyForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      emailAddress: "",
      phoneNumber: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
      website: ""
    },
  })

  const { toast } = useToast();

  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      const response = await CompanyService.addCompany(selectedBusiness.id, values);
      const result = response.data;
      if (result.result === "success") {
        showToastWithTimeout("success", "Success", "Company added successfully", 3000);
        setTimeout(() => {
          window.location.href = '/companies';
        }, 1500);
      } else {
        showToastWithTimeout("error", "Error", result.message || "Failed to add company", 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "Error", "Failed to add company", 3000);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="flex flex-col space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Add New Company</h1>
            <p className="text-slate-500 mt-1">Create a new company for your business</p>
          </div>
          <Button 
            onClick={() => window.history.back()} 
            variant="outline" 
            className="border-slate-200 text-slate-700 hover:bg-slate-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-medium flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Company Name <span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="Enter company name" {...field} className="border-slate-300 focus:border-blue-500 focus:ring-blue-500" />
                        </FormControl>
                        <FormDescription className="text-slate-500">
                          Enter the company name
                        </FormDescription>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emailAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Email Address</FormLabel>
                        <FormControl>
                          <Input placeholder="company@example.com" {...field} className="border-slate-300 focus:border-blue-500 focus:ring-blue-500" />
                        </FormControl>
                        <FormDescription className="text-slate-500">
                          Enter the company's email address
                        </FormDescription>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Phone Number</FormLabel>
                        <FormControl>
                          <PhoneInput placeholder="+1234567890" {...field} className="w-full rounded-md border border-slate-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </FormControl>
                        <FormDescription className="text-slate-500">
                          Enter the company's phone number
                        </FormDescription>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Website</FormLabel>
                        <FormControl>
                          <Input placeholder="https://example.com" {...field} className="border-slate-300 focus:border-blue-500 focus:ring-blue-500" />
                        </FormControl>
                        <FormDescription className="text-slate-500">
                          Enter the company's website
                        </FormDescription>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <div className="md:col-span-2">
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700">Address</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Enter address" {...field} className="min-h-[80px] border-slate-300 focus:border-blue-500 focus:ring-blue-500" />
                          </FormControl>
                          <FormDescription className="text-slate-500">
                            Enter the company's full address
                          </FormDescription>
                          <FormMessage className="text-red-500" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">City</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter city" {...field} className="border-slate-300 focus:border-blue-500 focus:ring-blue-500" />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">State/Province</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter state or province" {...field} className="border-slate-300 focus:border-blue-500 focus:ring-blue-500" />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Postal Code</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter postal code" {...field} className="border-slate-300 focus:border-blue-500 focus:ring-blue-500" />
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Country</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <SelectTrigger className="border-slate-300 focus:ring-blue-500 focus:border-blue-500">
                              <SelectValue placeholder="Select a country" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px] overflow-y-auto bg-white border border-slate-200">
                              {countries.map((country: any) => (
                                <SelectItem key={country} value={country}>
                                  {country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-end mt-8">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => window.history.back()} 
                    className="mr-4 border-slate-300 text-slate-700 hover:bg-slate-100"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className=""
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...
                      </>
                    ) : (
                      <>
                        <SendIcon className="mr-2 h-4 w-4" /> Create Company
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
